/**
 * ApplyThemeGroupsDialog - Shared confirm dialog for selective theme apply
 *
 * Two-step dialog used by both apply surfaces (theme editor "Apply to canvas"
 * and canvas Bulk Actions "Update UI from theme"):
 *
 * 1. Groups step — pick attribute groups (Background / Header / Footer / Body).
 *    Groups outside the union of the selected screens' supportedGroups are
 *    greyed out with a reason caption. Prominent source + scope labels.
 * 2. Confirm step — engine result rows with before/after thumbnails,
 *    ⚠️ chip on customized screens and a per-row opt-out checkbox.
 *
 * The engine is injected via props (defaults to applyThemeGroupsToScreens)
 * so the dialog stays decoupled and unit-testable. The dialog never persists —
 * the parent receives the final selection in onConfirm.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SDialog, type DialogActionConfig } from '@modules/ui-components';
import { V2ScreenView, AutoScaleScreenContainer } from '@modules/screen-view-v2';
import { DeviceMockup } from '@modules/device-mockup';
import { FontConfigFromProjectProvider } from '@modules/font-config';
import { ThemeV2EditorView } from './ThemeV2EditorView';
import type { ThemeV2 } from '../types/themesV2.types';
import {
  ALL_THEME_APPLY_GROUPS,
  THEME_APPLY_GROUP_LABELS,
  supportedGroups,
  type ThemeApplyGroup,
} from '../mappers/themeApplyGroups';
import {
  applyThemeGroupsToScreens,
  type ApplyThemeGroupsResult,
  type ThemeApplyScreenChange,
  type ThemeApplyScreenLike,
} from '../mappers/applyThemeGroupsToScreens';
import type { StepNode } from '@segmently/schema-models/v2';

export interface ApplyThemeGroupsConfirmPayload<T extends ThemeApplyScreenLike> {
  /** Changes the user confirmed (opt-outs already removed). */
  changes: ThemeApplyScreenChange<T>[];
  groups: ThemeApplyGroup[];
  skippedNoop: T[];
  unsupported: ApplyThemeGroupsResult<T>['unsupported'];
  /** Changes the user explicitly opted out of. */
  optedOut: ThemeApplyScreenChange<T>[];
  /** The theme actually applied — equals the edited theme when editing is enabled. */
  theme: ThemeV2;
  /** True when the user adjusted the theme inside the dialog (edit mode). */
  themeWasEdited: boolean;
  /**
   * Whether the edited theme should be persisted to the onboarding theme.
   * False = temporary theme: screens are updated but the saved theme is untouched.
   * Always true outside edit mode.
   */
  saveTheme: boolean;
}

export interface ApplyThemeGroupsDialogProps<T extends ThemeApplyScreenLike> {
  open: boolean;
  onClose: () => void;
  /** Theme providing the values to apply. */
  theme: ThemeV2;
  /** Live target screens, already scoped by type or hand-picked selection. */
  screens: T[];
  /** Where the theme values come from, e.g. "Draft (unsaved)". */
  sourceLabel: string;
  /** What the apply targets, e.g. "Screen types: Paywall, List" or "3 selected screens". */
  scopeLabel: string;
  /** Called with the confirmed selection; parent persists and closes. */
  onConfirm: (payload: ApplyThemeGroupsConfirmPayload<T>) => Promise<void> | void;
  /** Engine override for tests; defaults to applyThemeGroupsToScreens. */
  computeApply?: typeof applyThemeGroupsToScreens;
  /** Per-kind template id preference (the clicked tile) for duplicate-kind themes. */
  templateIdByKind?: Record<string, string>;
  /** Locale for screen thumbnails. */
  locale?: string;
  initialGroups?: ThemeApplyGroup[];
  /**
   * Enable an "Edit theme" step before the groups step. The user can adjust the
   * theme (colors/fonts/background/header/footer) and the edited theme is what
   * gets applied. Used by the canvas "Edit theme & apply" bulk action.
   */
  allowThemeEdit?: boolean;
  /** Project ID — needed by the embedded editor for per-project font config. */
  projectId?: string;
}

function formatStepKind(screenType: string): string {
  return screenType.replace(/([A-Z])/g, ' $1').trim();
}

function screenKey(screen: ThemeApplyScreenLike, index: number): string {
  const candidate = screen as unknown as { entityId?: string; id?: string };
  return candidate.entityId ?? candidate.id ?? `index-${index}`;
}

function screenDisplayName(screen: ThemeApplyScreenLike): string {
  const candidate = screen as unknown as { name?: string };
  return candidate.name ?? formatStepKind(screen.screenType as string);
}

function formatApplyError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return 'Theme apply failed. Please try again.';
}

const THUMBNAIL_SX = { width: 110, height: 238, flexShrink: 0 } as const;

function ScreenThumbnail({
  screen,
  locale,
}: {
  screen: ThemeApplyScreenLike;
  locale: string;
}) {
  return (
    <Box sx={{ ...THUMBNAIL_SX, pointerEvents: 'none' }}>
      <DeviceMockup device="iphone" autoScale scaleOptions={{ maxScale: 0.4, padding: 2 }}>
        <AutoScaleScreenContainer>
          <V2ScreenView step={screen as unknown as StepNode} locale={locale} previewMode="edit" />
        </AutoScaleScreenContainer>
      </DeviceMockup>
    </Box>
  );
}

export function ApplyThemeGroupsDialog<T extends ThemeApplyScreenLike>({
  open,
  onClose,
  theme,
  screens,
  sourceLabel,
  scopeLabel,
  onConfirm,
  computeApply = applyThemeGroupsToScreens,
  templateIdByKind,
  locale = 'en-US',
  initialGroups,
  allowThemeEdit = false,
  projectId,
}: ApplyThemeGroupsDialogProps<T>) {
  type Step = 'edit' | 'groups' | 'confirm';
  const [step, setStep] = useState<Step>(allowThemeEdit ? 'edit' : 'groups');
  const [selectedGroups, setSelectedGroups] = useState<ThemeApplyGroup[]>(
    initialGroups ?? [...ALL_THEME_APPLY_GROUPS]
  );
  const [result, setResult] = useState<ApplyThemeGroupsResult<T> | null>(null);
  const [optedOutKeys, setOptedOutKeys] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  // Local editable copy of the theme (edit mode only)
  const [editedTheme, setEditedTheme] = useState<ThemeV2>(theme);
  const [themeWasEdited, setThemeWasEdited] = useState(false);
  // Whether to persist the edited theme (edit mode only). Off = temporary theme.
  const [saveTheme, setSaveTheme] = useState(true);

  // Re-seed the editable theme whenever the dialog (re)opens with a new theme
  useEffect(() => {
    if (open) {
      setEditedTheme(theme);
      setThemeWasEdited(false);
      setSaveTheme(true);
      setStep(allowThemeEdit ? 'edit' : 'groups');
      setApplyError(null);
    }
  }, [open, theme, allowThemeEdit]);

  const handleThemeEdit = useCallback((updated: ThemeV2) => {
    setEditedTheme(updated);
    setThemeWasEdited(true);
  }, []);

  const appliedTheme = allowThemeEdit ? editedTheme : theme;

  // A group is available when at least one target screen's StepKind supports it.
  const availableGroups = useMemo(() => {
    const union = new Set<ThemeApplyGroup>();
    for (const screen of screens) {
      for (const group of supportedGroups(screen.screenType as StepNode['screenType'])) {
        union.add(group);
      }
    }
    return union;
  }, [screens]);

  const effectiveGroups = useMemo(
    () => selectedGroups.filter((group) => availableGroups.has(group)),
    [selectedGroups, availableGroups]
  );

  const resetAndClose = useCallback(() => {
    setStep(allowThemeEdit ? 'edit' : 'groups');
    setResult(null);
    setOptedOutKeys(new Set());
    setApplying(false);
    setApplyError(null);
    onClose();
  }, [onClose, allowThemeEdit]);

  const handleDialogClose = useCallback(() => {
    if (applying) return;
    resetAndClose();
  }, [applying, resetAndClose]);

  const handleToggleGroup = useCallback((group: ThemeApplyGroup) => {
    setSelectedGroups((current) =>
      current.includes(group)
        ? current.filter((g) => g !== group)
        : [...current, group]
    );
  }, []);

  const handleContinue = useCallback(() => {
    const computed = computeApply(appliedTheme, effectiveGroups, screens, { templateIdByKind });
    setResult(computed);
    setOptedOutKeys(new Set());
    setApplyError(null);
    setStep('confirm');
  }, [computeApply, appliedTheme, effectiveGroups, screens, templateIdByKind]);

  const handleToggleOptOut = useCallback((key: string) => {
    setOptedOutKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (!result || applying) return;
    const confirmed: ThemeApplyScreenChange<T>[] = [];
    const optedOut: ThemeApplyScreenChange<T>[] = [];
    result.changed.forEach((change, index) => {
      if (optedOutKeys.has(screenKey(change.before, index))) optedOut.push(change);
      else confirmed.push(change);
    });
    setApplyError(null);
    setApplying(true);
    try {
      await onConfirm({
        changes: confirmed,
        groups: effectiveGroups,
        skippedNoop: result.skippedNoop,
        unsupported: result.unsupported,
        optedOut,
        theme: appliedTheme,
        themeWasEdited,
        saveTheme: allowThemeEdit ? saveTheme : true,
      });
      resetAndClose();
    } catch (error) {
      console.error('[ApplyThemeGroupsDialog] Theme apply failed:', error);
      setApplyError(formatApplyError(error));
    } finally {
      setApplying(false);
    }
  }, [result, applying, optedOutKeys, onConfirm, effectiveGroups, resetAndClose, appliedTheme, themeWasEdited, allowThemeEdit, saveTheme]);

  const confirmedCount = result
    ? result.changed.filter((change, index) => !optedOutKeys.has(screenKey(change.before, index))).length
    : 0;

  // Dismiss is the standard SDialog header X (top-left) — no duplicate Cancel button.

  // Right-aligned Back (only when a previous step exists).
  const backAction: DialogActionConfig | undefined =
    step === 'confirm'
      ? {
          label: 'Back',
          onClick: () => setStep('groups'),
          variant: 'outlined',
          disabled: applying,
          'data-testid': 'apply-theme-back-btn',
        }
      : step === 'groups' && allowThemeEdit
        ? {
            label: 'Back',
            onClick: () => setStep('edit'),
            variant: 'outlined',
            disabled: applying,
            'data-testid': 'apply-theme-edit-back-btn',
          }
        : undefined;

  // Right-aligned primary CTA per step.
  const primaryAction: DialogActionConfig =
    step === 'edit'
      ? {
          label: 'Next',
          onClick: () => setStep('groups'),
          disabled: screens.length === 0,
          'data-testid': 'apply-theme-edit-next-btn',
        }
      : step === 'groups'
        ? {
            label: 'Continue',
            onClick: handleContinue,
            disabled: effectiveGroups.length === 0 || screens.length === 0,
            'data-testid': 'apply-theme-continue-btn',
          }
        : {
            label: `Apply${confirmedCount > 0 ? ` (${confirmedCount})` : ''}`,
            onClick: handleApply,
            disabled: applying || confirmedCount === 0,
            loading: applying,
            'data-testid': 'apply-theme-confirm-btn',
          };

  const dialogContent = (
    <SDialog
      open={open}
      onClose={handleDialogClose}
      title={allowThemeEdit ? 'Edit theme & apply to selected screens' : 'Apply theme to canvas'}
      maxWidth={step === 'edit' ? 'xl' : step === 'confirm' ? 'lg' : 'md'}
      fullWidth
      slotProps={{
        paper: {
          // Tall paper for the screen-heavy steps so the list has room and
          // scrolls inside DialogContent; auto height for the brief groups step.
          sx: step === 'groups' ? undefined : { height: '85vh', maxHeight: '85vh' },
        },
      }}
      secondaryAction={backAction}
      primaryAction={primaryAction}
      showCloseButton={!applying}
      data-testid="apply-theme-groups-dialog"
    >
      <Box sx={{ position: 'relative', minHeight: 220 }}>
        {applying && (
          <Box
            data-testid="apply-theme-applying-overlay"
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(28, 22, 62, 0.78)',
              backdropFilter: 'blur(2px)',
              borderRadius: 1,
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.primary">
                Applying theme to canvas...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Keep this dialog open until the update finishes.
              </Typography>
            </Stack>
          </Box>
        )}

        <Stack spacing={2} sx={{ minHeight: 220, px: 2 }}>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Source: ${sourceLabel}`}
              data-testid="apply-theme-source-label"
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Scope: ${scopeLabel}`}
              data-testid="apply-theme-scope-label"
            />
          </Stack>

          {applying && step === 'confirm' && (
            <Alert
              severity="info"
              icon={<CircularProgress size={18} />}
              data-testid="apply-theme-applying-alert"
            >
              Applying {confirmedCount} screen update{confirmedCount === 1 ? '' : 's'}.
              Controls are disabled until the operation finishes.
            </Alert>
          )}

          {applyError && (
            <Alert severity="error" data-testid="apply-theme-error-alert">
              Theme apply failed: {applyError}
            </Alert>
          )}

          {step === 'edit' && (
            <Box sx={{ height: 560, minHeight: 0 }} data-testid="apply-theme-edit-step">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Adjust the theme below, then choose what to apply to the {screens.length} selected screen
                  {screens.length === 1 ? '' : 's'}.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveTheme}
                      disabled={applying}
                      onChange={(event) => setSaveTheme(event.target.checked)}
                      data-testid="apply-theme-save-toggle"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {saveTheme ? 'Save theme changes' : 'Temporary theme (don’t save)'}
                    </Typography>
                  }
                />
              </Stack>
              <Box sx={{ height: 'calc(100% - 36px)', minHeight: 0 }}>
                <ThemeV2EditorView
                  theme={editedTheme}
                  onChange={handleThemeEdit}
                  projectId={projectId}
                />
              </Box>
            </Box>
          )}

          {step === 'groups' && (
            <>
              <Typography variant="body2" color="text.secondary">
                Choose which theme attributes to push onto the target screens.
              </Typography>
              <FormGroup>
                {ALL_THEME_APPLY_GROUPS.map((group) => {
                  const available = availableGroups.has(group);
                  const control = (
                    <FormControlLabel
                      key={group}
                      disabled={!available}
                      control={
                        <Checkbox
                          checked={available && selectedGroups.includes(group)}
                          disabled={applying}
                          onChange={() => handleToggleGroup(group)}
                          data-testid={`apply-theme-group-${group}`}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{THEME_APPLY_GROUP_LABELS[group]}</Typography>
                          {!available && (
                            <Typography variant="caption" color="text.disabled">
                              Not supported by the selected screen types
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  );
                  return available ? (
                    control
                  ) : (
                    <Tooltip key={group} title="None of the selected screen types support this group">
                      <span>{control}</span>
                    </Tooltip>
                  );
                })}
              </FormGroup>
            </>
          )}

          {step === 'confirm' && result && (
            <>
              {result.changed.length === 0 ? (
                <Alert severity="info">
                  Nothing to change — {result.skippedNoop.length} screen(s) already match the theme
                  {result.unsupported.length > 0
                    ? `, ${result.unsupported.length} unsupported`
                    : ''}
                  .
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {result.changed.length} screen(s) will be updated
                    {result.skippedNoop.length > 0 ? ` · ${result.skippedNoop.length} already match` : ''}
                    {result.unsupported.length > 0 ? ` · ${result.unsupported.length} unsupported` : ''}.
                    Uncheck a screen to keep it as is.
                  </Typography>
                  <Stack spacing={2} data-testid="apply-theme-confirm-rows">
                    {result.changed.map((change, index) => {
                      const key = screenKey(change.before, index);
                      const included = !optedOutKeys.has(key);
                      return (
                        <Stack
                          key={key}
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          data-testid={`apply-theme-confirm-row-${key}`}
                          data-classification={change.classification}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            opacity: included ? 1 : 0.5,
                          }}
                        >
                          <Checkbox
                            checked={included}
                            disabled={applying}
                            onChange={() => handleToggleOptOut(key)}
                            data-testid={`apply-theme-screen-optout-${key}`}
                          />
                          <Stack spacing={0.5} sx={{ minWidth: 140, flexGrow: 1 }}>
                            <Typography variant="body2" noWrap>
                              {screenDisplayName(change.before)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatStepKind(change.before.screenType as string)} ·{' '}
                              {change.appliedGroups.map((g) => THEME_APPLY_GROUP_LABELS[g]).join(', ')}
                            </Typography>
                            {change.classification === 'customized' && (
                              <Chip
                                size="small"
                                color="warning"
                                icon={<WarningAmberIcon />}
                                label="Customized — will be overwritten"
                                sx={{ alignSelf: 'flex-start' }}
                              />
                            )}
                            {change.clippedGroups.length > 0 && (
                              <Typography variant="caption" color="text.disabled">
                                Skipped here: {change.clippedGroups.map((g) => THEME_APPLY_GROUP_LABELS[g]).join(', ')}
                              </Typography>
                            )}
                          </Stack>
                          <ScreenThumbnail screen={change.before} locale={locale} />
                          <ArrowForwardIcon color="action" />
                          <ScreenThumbnail screen={change.after} locale={locale} />
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
    </SDialog>
  );

  // Standalone hosts (e.g. BlockCanvasEditor) have no FontConfig provider
  // above this dialog — self-wrap so screen thumbnails honor the project's
  // font mappings and load web fonts. Harmless double-wrap under
  // OnboardingCanvasEditorV2 (same values; the font loader dedups).
  if (projectId) {
    return (
      <FontConfigFromProjectProvider projectId={projectId}>
        {dialogContent}
      </FontConfigFromProjectProvider>
    );
  }
  return dialogContent;
}

export default ApplyThemeGroupsDialog;
